import React, { useState, useCallback, useEffect } from 'react';
import TaskCard from './TaskCard';

const KANBAN_COLUMNS = [
    { id: 'To Do', title: 'To Do', color: 'bg-gray-100' },
    { id: 'In Progress', title: 'In Progress', color: 'bg-blue-100' },
    { id: 'Review', title: 'Review', color: 'bg-yellow-100' },
    { id: 'Done', title: 'Done', color: 'bg-green-100' }
];

function KanbanBoard({ tasks = [], onTaskClick, onStatusChange, onReorder, loading = false }) {
    const [draggedTask, setDraggedTask] = useState(null);
    const [draggedOverColumn, setDraggedOverColumn] = useState(null);
    const [draggedOverTaskId, setDraggedOverTaskId] = useState(null);
    const [tasksByStatus, setTasksByStatus] = useState({});

    // Group tasks by kanban status
    useEffect(() => {
        const grouped = {};
        KANBAN_COLUMNS.forEach(col => {
            grouped[col.id] = [];
        });
        
        tasks.forEach(task => {
            const status = task.kanban_status || 'To Do';
            if (grouped[status]) {
                grouped[status].push(task);
            } else {
                grouped['To Do'].push(task); // Fallback to 'To Do'
            }
        });

        // Sort tasks within each column:
        // 1) explicit kanban_order (if present)
        // 2) due date (soonest first)
        // 3) priority (urgent first)
        Object.keys(grouped).forEach(status => {
            grouped[status].sort((a, b) => {
                const orderA = a.kanban_order !== undefined && a.kanban_order !== null ? parseInt(a.kanban_order) : null;
                const orderB = b.kanban_order !== undefined && b.kanban_order !== null ? parseInt(b.kanban_order) : null;
                if (orderA !== null && orderB !== null && !Number.isNaN(orderA) && !Number.isNaN(orderB) && orderA !== orderB) {
                    return orderA - orderB;
                }

                // First sort by due date
                if (a.due_date && !b.due_date) return -1;
                if (!a.due_date && b.due_date) return 1;
                if (a.due_date && b.due_date) {
                    const dateA = new Date(a.due_date);
                    const dateB = new Date(b.due_date);
                    if (dateA.getTime() !== dateB.getTime()) {
                        return dateA.getTime() - dateB.getTime();
                    }
                }
                
                // Then sort by priority
                const priorityOrder = { 'Urgent': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
                const priorityA = priorityOrder[a.priority] ?? 4;
                const priorityB = priorityOrder[b.priority] ?? 4;
                return priorityA - priorityB;
            });
        });

        setTasksByStatus(grouped);
    }, [tasks]);

    const handleDragStart = useCallback((e, task) => {
        setDraggedTask(task);
        setDraggedOverTaskId(null);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e, columnId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDraggedOverColumn(columnId);
    }, []);

    const handleDragOverTask = useCallback((e, columnId, taskId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDraggedOverColumn(columnId);
        setDraggedOverTaskId(taskId);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDraggedOverColumn(null);
        setDraggedOverTaskId(null);
    }, []);

    const handleDrop = useCallback((e, columnId) => {
        e.preventDefault();
        
        if (!draggedTask) {
            setDraggedTask(null);
            setDraggedOverColumn(null);
            setDraggedOverTaskId(null);
            return;
        }

        // Dropped on the column (not on a specific card) -> move to bottom of destination column
        const fromStatus = draggedTask.kanban_status || 'To Do';
        const toStatus = columnId;
        const sourceList = tasksByStatus[fromStatus] || [];
        const destList = tasksByStatus[toStatus] || [];
        const fromIdx = sourceList.findIndex(t => t.id === draggedTask.id);

        // Same-column: allow "drop on whitespace" to move card to bottom
        if (fromStatus === toStatus) {
            if (fromIdx < 0) {
                setDraggedTask(null);
                setDraggedOverColumn(null);
                setDraggedOverTaskId(null);
                return;
            }
            if (fromIdx === sourceList.length - 1) {
                setDraggedTask(null);
                setDraggedOverColumn(null);
                setDraggedOverTaskId(null);
                return;
            }

            if (onReorder) {
                const nextList = [...sourceList];
                const [moved] = nextList.splice(fromIdx, 1);
                nextList.push(moved);
                setTasksByStatus({ ...tasksByStatus, [toStatus]: nextList });

                const updates = [];
                nextList.forEach((t, idx) => updates.push({ id: t.id, kanbanOrder: (idx + 1) * 1000 }));
                onReorder(updates);
            }
        } else if (fromIdx >= 0) {
            const nextSource = [...sourceList];
            const [movedRaw] = nextSource.splice(fromIdx, 1);
            // Optimistically update status for visuals immediately
            const moved = {
                ...movedRaw,
                kanban_status: toStatus,
                status: toStatus,
                completed_date: toStatus === 'Done' ? (movedRaw.completed_date || new Date().toISOString()) : null
            };
            const nextDest = [...destList, moved];
            const nextGrouped = { ...tasksByStatus, [toStatus]: nextDest, [fromStatus]: nextSource };
            setTasksByStatus(nextGrouped);

            if (onReorder) {
                const updates = [];
                nextDest.forEach((t, idx) => updates.push({ id: t.id, kanbanOrder: (idx + 1) * 1000 }));
                nextSource.forEach((t, idx) => updates.push({ id: t.id, kanbanOrder: (idx + 1) * 1000 }));
                onReorder(updates);
            }

            // Persist status change
            if (onStatusChange) onStatusChange(draggedTask, columnId);
        }

        setDraggedTask(null);
        setDraggedOverColumn(null);
        setDraggedOverTaskId(null);
    }, [draggedTask, onStatusChange, onReorder, tasksByStatus]);

    const handleDropOnTask = useCallback((e, columnId, targetTaskId) => {
        e.preventDefault();
        if (!draggedTask) return;

        const fromStatus = draggedTask.kanban_status || 'To Do';
        const toStatus = columnId;

        const sourceList = tasksByStatus[fromStatus] || [];
        const destList = tasksByStatus[toStatus] || [];
        const fromIdx = sourceList.findIndex(t => t.id === draggedTask.id);
        const toIdx = destList.findIndex(t => t.id === targetTaskId);

        if (fromIdx < 0 || toIdx < 0) {
            setDraggedTask(null);
            setDraggedOverColumn(null);
            setDraggedOverTaskId(null);
            return;
        }

        // Build next lists
        const nextSource = [...sourceList];
        const [movedRaw] = nextSource.splice(fromIdx, 1);
        // Optimistically update status for visuals immediately when cross-column
        const moved = fromStatus !== toStatus
            ? {
                ...movedRaw,
                kanban_status: toStatus,
                status: toStatus,
                completed_date: toStatus === 'Done' ? (movedRaw.completed_date || new Date().toISOString()) : null
            }
            : movedRaw;
        const nextDest = (fromStatus === toStatus) ? nextSource : [...destList];
        const adjustedToIdx = (fromStatus === toStatus && fromIdx < toIdx) ? Math.max(0, toIdx - 1) : toIdx;
        nextDest.splice(adjustedToIdx, 0, moved);

        const nextGrouped = { ...tasksByStatus };
        nextGrouped[toStatus] = nextDest;
        if (fromStatus !== toStatus) nextGrouped[fromStatus] = nextSource;
        setTasksByStatus(nextGrouped);

        // Persist ordering (1000 spacing) for the affected columns
        if (onReorder) {
            const updates = [];
            const pushUpdates = (list) => {
                list.forEach((t, idx) => updates.push({ id: t.id, kanbanOrder: (idx + 1) * 1000 }));
            };
            pushUpdates(nextDest);
            if (fromStatus !== toStatus) pushUpdates(nextSource);
            onReorder(updates);
        }

        // Persist status change if moved across columns
        if (fromStatus !== toStatus && onStatusChange) {
            onStatusChange(draggedTask, toStatus);
        }

        setDraggedTask(null);
        setDraggedOverColumn(null);
        setDraggedOverTaskId(null);
    }, [draggedTask, tasksByStatus, onStatusChange, onReorder]);

    const handleDragEnd = useCallback(() => {
        setDraggedTask(null);
        setDraggedOverColumn(null);
        setDraggedOverTaskId(null);
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-gray-500">Loading tasks...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-row gap-4 overflow-x-auto pb-4">
            {KANBAN_COLUMNS.map(column => {
                const columnTasks = tasksByStatus[column.id] || [];
                const isDraggedOver = draggedOverColumn === column.id;
                
                return (
                    <div
                        key={column.id}
                        className={`flex-1 min-w-[280px] rounded-lg ${column.color} p-4 ${
                            isDraggedOver ? 'ring-2 ring-blue-400' : ''
                        }`}
                        onDragOver={(e) => handleDragOver(e, column.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        {/* Column Header */}
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-800">
                                {column.title}
                            </h3>
                            <span className="bg-white text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                                {columnTasks.length}
                            </span>
                        </div>

                        {/* Tasks */}
                        <div className="space-y-2 min-h-[100px]">
                            {columnTasks.length === 0 ? (
                                <div className="text-gray-400 text-sm text-center py-4">
                                    No tasks
                                </div>
                            ) : (
                                columnTasks.map(task => (
                                    <div
                                        key={task.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, task)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOverTask(e, column.id, task.id)}
                                        onDrop={(e) => handleDropOnTask(e, column.id, task.id)}
                                        className={`${draggedTask?.id === task.id ? 'opacity-50' : ''} ${draggedOverTaskId === task.id ? 'ring-2 ring-blue-400 rounded-lg' : ''}`}
                                    >
                                        <TaskCard
                                            task={task}
                                            onClick={() => onTaskClick && onTaskClick(task)}
                                            onToggleComplete={(t, checked) => {
                                                if (!onStatusChange) return;
                                                onStatusChange(t, checked ? 'Done' : 'To Do');
                                            }}
                                        />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default KanbanBoard;

